import type {Metadata} from "next";

import {isProduction, isStaging} from "@/shared/utils/env/env";

import {DEFAULT_SEO_CONFIG} from "./config";

export interface OpenGraphConfig {
    title?: string;
    description?: string;
    image?: string;
    type?: "website" | "article";
    url?: string;
    publishedTime?: string;
}

export interface TwitterConfig {
    title?: string;
    description?: string;
    image?: string;
    card?: "summary" | "summary_large_image";
    creator?: string;
}

export interface MetaConfig {
    title?: string;
    description?: string;
    keywords?: Array<string>;
}

export interface ArticleConfig {
    publishedTime?: string;
    modifiedTime?: string;
    authors?: Array<string>;
    tags?: Array<string>;
}

export interface RobotsConfig {
    index?: boolean;
    follow?: boolean;
    noindex?: boolean;
    nofollow?: boolean;
}

export class Seo {
    protected readonly baseUrl?: string;
    private readonly defaultTitle: string;
    private readonly defaultDescription: string;
    private readonly siteName: string;
    private readonly defaultOgImage: string;
    private readonly twitterSite: string;

    // Internal state
    private metadata: Metadata = {};
    private currentTitle?: string;
    private currentDescription?: string;
    private currentKeywords?: Array<string>;
    private currentCanonical?: string;
    private currentOgImage?: string;

    constructor() {
        this.baseUrl = DEFAULT_SEO_CONFIG.baseUrl;
        this.defaultTitle = DEFAULT_SEO_CONFIG.defaultTitle;
        this.defaultDescription = DEFAULT_SEO_CONFIG.defaultDescription;
        this.siteName = DEFAULT_SEO_CONFIG.siteName;
        this.defaultOgImage = DEFAULT_SEO_CONFIG.defaultOgImage;
        this.twitterSite = DEFAULT_SEO_CONFIG.twitterSite;

        // Automatically set index
        this.metadata.robots = {
            index: isProduction(),
            follow: isProduction(),
        };
    }

    /**
     * Add basic meta tags (title, description, keywords)
     */
    addMeta(config: MetaConfig): this {
        this.currentTitle = config.title || this.defaultTitle;
        this.currentDescription = config.description || this.defaultDescription;
        this.currentKeywords = config.keywords;

        this.metadata.title = this.currentTitle;
        this.metadata.description = this.currentDescription;

        if (this.currentKeywords && this.currentKeywords.length > 0) {
            this.metadata.keywords = this.currentKeywords;
        }

        return this;
    }

    /**
     * Add canonical URL
     */
    addCanonical(url: string): this {
        this.currentCanonical = url;
        this.metadata.alternates = {
            ...this.metadata.alternates,
            canonical: this.getFullUrl(url),
        };
        return this;
    }

    /**
     * Add Open Graph metadata
     */
    addOpenGraph(config?: OpenGraphConfig): this {
        const ogTitle = config?.title || this.currentTitle || this.defaultTitle;
        const ogDescription = config?.description || this.currentDescription || this.defaultDescription;
        const ogImage = config?.image || this.currentOgImage || this.defaultOgImage;
        const ogUrl = config?.url || this.currentCanonical;

        const images = ogImage ? [{url: this.getFullUrl(ogImage)}] : undefined;

        this.metadata.openGraph = {
            title: ogTitle,
            description: ogDescription,
            siteName: this.siteName,
            type: config?.type || "website",
            url: ogUrl ? this.getFullUrl(ogUrl) : undefined,
            images,
            ...(config?.publishedTime && {publishedTime: config.publishedTime}),
        };

        return this;
    }

    /**
     * Add published time for Open Graph metadata
     * Works with any Open Graph type (website, article, etc.)
     */
    addPublishedTime(publishedTime?: string): this {
        if (!publishedTime) {
            return this;
        }

        if (!this.metadata.openGraph) {
            this.addOpenGraph();
        }

        this.metadata.openGraph = {
            ...this.metadata.openGraph,
            publishedTime,
        } as typeof this.metadata.openGraph;

        return this;
    }

    /**
     * Add Twitter metadata
     */
    addTwitter(config?: TwitterConfig): this {
        const twitterTitle = config?.title || this.currentTitle || this.defaultTitle;
        const twitterDescription = config?.description || this.currentDescription || this.defaultDescription;
        const twitterImage = config?.image || this.currentOgImage || this.defaultOgImage;

        this.metadata.twitter = {
            card: config?.card || "summary_large_image",
            title: twitterTitle,
            description: twitterDescription,
            site: this.twitterSite,
            creator: config?.creator,
            images: twitterImage ? [this.getFullUrl(twitterImage)] : undefined,
        };

        return this;
    }

    /**
     * Add robots configuration (will override auto noindex for non-production)
     */
    addRobots(config: RobotsConfig): this {
        const index = config.noindex ? false : config.index !== false;
        const follow = config.nofollow ? false : config.follow !== false;

        this.metadata.robots = {
            index,
            follow,
        };

        return this;
    }

    addImage(image?: string): this {
        return this.setImage(image);
    }
    /**
     * Set Open Graph image
     */
    setImage(image?: string): this {
        if (!image) {
            return this;
        }
        this.currentOgImage = image;
        return this;
    }

    /**
     * Add Open Graph article metadata
     */
    addArticle(config: ArticleConfig): this {
        if (!this.metadata.openGraph) {
            this.addOpenGraph({type: "article"});
        }

        this.metadata.openGraph = {
            ...this.metadata.openGraph,
            type: "article",
            publishedTime: config.publishedTime,
            modifiedTime: config.modifiedTime,
            authors: config.authors,
            tags: config.tags,
        };

        return this;
    }

    /**
     * Add multiple images for Open Graph
     */
    addImages(images: Array<{url: string; width?: number; height?: number; alt?: string}>): this {
        if (!this.metadata.openGraph) {
            this.addOpenGraph();
        }

        this.metadata.openGraph = {
            ...this.metadata.openGraph,
            images: images.map((img) => ({
                url: this.getFullUrl(img.url),
                width: img.width,
                height: img.height,
                alt: img.alt,
            })),
        };

        return this;
    }

    /**
     * Add custom metadata field
     */
    addCustom(key: string, value: unknown): this {
        (this.metadata as Record<string, unknown>)[key] = value;
        return this;
    }

    /**
     * Build and return final Metadata object
     */
    build(): Metadata {
        if (isStaging()) {
            this.metadata.robots = {index: false, follow: false};
        }
        return {...this.metadata};
    }

    /**
     * Fetch data for SEO metadata
     * Override this method in subclasses that need to fetch data
     * @throws Error if not implemented in subclass
     */
    async fetchData(): Promise<void> {
        throw new Error(
            `fetchData() method is not implemented in ${this.constructor.name}. ` +
                "Override this method in your subclass if you need to fetch data for SEO metadata.",
        );
    }

    /**
     * Static method to return 404 not found metadata
     * Use this in catch blocks of generateMetadata functions
     */
    static notFoundMetadata(config?: {title?: string; description?: string}): Metadata {
        return {
            title: config?.title || "Page Not Found",
            description: config?.description || "The page you are looking for could not be found.",
            robots: {
                index: false,
                follow: false,
            },
        };
    }

    /**
     * Convert relative URL to absolute URL
     */
    private getFullUrl(path: string): string {
        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }
        return `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
    }
}
